import { LoadingStateChart } from 'components/analytics';
import { ChartBase } from 'components/common';
import { ExportIcon } from 'components/icons';
import { CardBackground } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import type { PurchaseTiming } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';

const ExportMenuLazy = lazy(() => import('./PurchaseTimingExportMenu'));

interface PurchaseTimingFirst30DaysProps {
  data: PurchaseTiming | undefined;
  loading: boolean;
}

export default function PurchaseTimingFirst30Days({
  data,
  loading,
}: PurchaseTimingFirst30DaysProps) {
  const exportContainerRef = useRef<HTMLDivElement>(null);

  const { currentStore } = useAppSelector(userDetailsState);

  const currency = currentStore.completeInfo?.currency ?? 'USD';

  /** Start Cards Data */
  const first_growth =
    data?.first_growth &&
    Object.keys(data.first_growth).length &&
    Object.keys(data.first_growth)[0];

  const first_choke =
    data?.first_choke &&
    Object.keys(data.first_choke).length &&
    Object.keys(data.first_choke)[0];
  const day1 =
    Object.values(data?.first_order_days || {}).length &&
    Object.values(data?.first_order_days || {})[0];
  const total_day30 =
    Object.values(data?.first_order_days || {}).length &&
    Object.values(data?.first_order_days || {}).reduce((x, y) => x + y, 0);

  const ordersOneTo30Days = ((total_day30 ?? 0) - (day1 ?? 0)).toFixed(2);
  const revOneTo30Days = data?.rev30d
    ? data.rev30d.toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : '--';

  // Ends Cards Data

  /** Chart Data Starts */

  // Thresholds is used as reference to slice the dataset into categories and display at different colors

  const minThreshold = first_growth ?? 1;
  const maxThreshold = first_choke ?? 1;

  // Subsets of data based on the original and threshold settings
  const active = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_days || {}).filter(
          ([key, _]) => parseInt(key) <= minThreshold
        )
      ),
    [data?.first_order_days, minThreshold]
  );

  const atRisk = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_days || {}).filter(
          ([key, _]) =>
            parseInt(key) > minThreshold && parseInt(key) < maxThreshold
        )
      ),
    [data?.first_order_days, minThreshold, maxThreshold]
  );

  const churn = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_days || {}).filter(
          ([key, _]) => parseInt(key) >= maxThreshold
        )
      ),
    [data?.first_order_days, maxThreshold]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Active',
          backgroundColor: '#4F92F7',
          borderColor: '#4F92F7',
          data: active,
        },
        {
          label: 'At risk',
          backgroundColor: '#F87349',
          borderColor: '#F87349',
          data: atRisk,
        },
        {
          label: 'Churned',
          backgroundColor: '#D5CBB7',
          borderColor: '#D5CBB7',
          data: churn,
        },
      ],
    }),
    [active, atRisk, churn]
  );

  const options = {
    interaction: {
      mode: 'nearest',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          text: 'Days from signup',
          display: true,
          font: { size: 15 },
        },
        stacked: true,
      },
      y: {
        ticks: {
          callback: (value: number) =>
            (value / 100).toLocaleString('en-US', {
              style: 'percent',
              minimumFractionDigits: value <= 1 ? 2 : 0,
              maximumFractionDigits: value <= 1 ? 2 : 0,
            }),
        },
        title: { text: '% purchased', display: true, font: { size: 15 } },
      },
    },
    plugins: {
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 20,
          font: {
            size: 15,
            lineHeight: '1.5px',
          },
        },
      },
      tooltip: {
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: (tooltip: { parsed: { y: string } }) => tooltip.parsed.y + '%',
        },
      },
    },
  };

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Days', key: 'period' },
      { label: 'Active', key: 'active' },
      { label: 'At risk', key: 'at_risk' },
      { label: 'Churn', key: 'churn' },
    ];

    const activeCSV = Object.entries(active).map(([key, value]) => ({
      period: key,
      active: value,
    }));

    const atRiskCSV = Object.entries(atRisk).map(([key, value]) => ({
      period: key,
      at_risk: value,
    }));

    const churnCSV = Object.entries(churn).map(([key, value]) => ({
      period: key,
      churn: value,
    }));

    const csvData = [...activeCSV, ...atRiskCSV, ...churnCSV];

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [active, atRisk, churn]);

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-purchase_timing-first_30_days-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  if (loading) return <LoadingStateChart chartTitle="First 30 days" />;
  if (!data?.first_order_days) return null;

  return (
    <div className="py-6" ref={exportContainerRef}>
      <div className="flex items-end justify-between px-2">
        <p className="text-lg leading-7 whitespace-nowrap">First 30 days</p>

        <Suspense
          fallback={
            <div className="flex items-center px-5 py-2.5 space-x-2 rounded-lg bg-segments-gray focus:outline-none">
              <p>Export</p>
              <ExportIcon className="w-5 h-5" />
            </div>
          }
        >
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Suspense>
      </div>

      <div className="mt-3">
        <CardBackground>
          <div className="p-10">
            <div className="flex justify-between">
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {(day1 ?? '--') + '%'}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Within 1 day
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {ordersOneTo30Days + '%'}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Between days 1-30
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">{revOneTo30Days}</p>
                <p className="text-base text-black text-opacity-50">
                  $ between days 1-30
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {'Day ' + (first_choke ?? '--')}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Conversions slow after
                </p>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-base text-black text-opacity-50">
                <span className="font-bold">{(day1 ?? '--') + '%'}</span> of
                customers purchase their first order within 1 day. After that,
                an additional
                <span className="font-bold">
                  {' '}
                  {ordersOneTo30Days + '%'}{' '}
                </span>{' '}
                of orders with
                <span className="font-bold"> {revOneTo30Days} </span>
                sales happen between days 1 and 30.
              </p>
            </div>
            <hr className="my-8" />
            <ChartBase type="bar" data={chartData} options={options} />
          </div>
        </CardBackground>
      </div>
    </div>
  );
}
